// 2026 power unit: combustion engine plus MGU-K, with the energy store and
// harvest limits from the FIA 2026 Technical Regulations (Section C Iss 20).
// See docs/research/fia-2026-regulations.md for sources.

import { param } from '../config/params';

const KW = 1000;
const MJ = 1e6;

export const powerParams = {
  icePowerKW: param({ id: 'pu.iceKW', label: 'ICE power (kW)', group: 'Power unit', min: 300, max: 500, step: 5, value: 400 }),
  harvestPerLapMJ: param({ id: 'pu.harvestLapMJ', label: 'Harvest limit / lap (MJ)', group: 'Power unit', min: 4, max: 9, step: 0.25, value: 7.5 }),
  coastHarvestKW: param({ id: 'pu.coastKW', label: 'Lift-off harvest (kW)', group: 'Power unit', min: 0, max: 350, step: 10, value: 120 }),
  deployFullBelowKmh: param({ id: 'pu.deployFullBelow', label: 'Full deploy below (km/h)', group: 'Power unit', min: 80, max: 320, step: 5, value: 160 }),
  deployHighSpeed: param({ id: 'pu.deployHigh', label: 'Deploy share at 320 km/h', group: 'Power unit', min: 0, max: 1, step: 0.05, value: 0.3 }),
  superClipKW: param({ id: 'pu.superClipKW', label: 'Super-clip harvest (kW)', group: 'Power unit', min: 0, max: 350, step: 10, value: 100 }),
};

/** MGU-K absolute power limit, C5.2.7. */
export const ERS_MAX_KW = 350;
/** Max state-of-charge window of the energy store, C5.2.9. */
export const STORE_WINDOW_MJ = 4;

/**
 * Maximum MGU-K propulsion power vs car speed, C5.2.8 i (normal) and ii (Overtake).
 * Speed in km/h, result in kW.
 */
export function ersDeployLimitKW(speedKmh: number, overtake = false): number {
  if (overtake) {
    return speedKmh < 355 ? Math.min(ERS_MAX_KW, 7100 - 20 * speedKmh) : 0;
  }
  if (speedKmh < 340) return Math.min(ERS_MAX_KW, 1800 - 5 * speedKmh);
  if (speedKmh < 345) return Math.max(0, 6900 - 20 * speedKmh);
  return 0;
}

/** Share of the permitted deployment used at a given speed (automatic energy strategy). */
export function deployWeight(speedKmh: number): number {
  const lo = powerParams.deployFullBelowKmh.value;
  const hi = 320;
  if (speedKmh <= lo) return 1;
  if (speedKmh >= hi) return powerParams.deployHighSpeed.value;
  return 1 + ((speedKmh - lo) / (hi - lo)) * (powerParams.deployHighSpeed.value - 1);
}

export interface PowerRequest {
  speed: number; // m/s
  throttle: number; // 0..1
  /** Braking force currently applied, N (used for regenerative harvest). */
  brakeForce: number;
  overtake: boolean;
  /**
   * Extra cap on MGU-K deployment, kW: the C5.2.8 iii "Alt 1" curve on specified
   * sectors in the race (250 kW below 310 km/h), or a wet-weather map.
   */
  deployCapKW?: number;
  /** ICE power share from the engine model's rpm curve (gearbox.ts); 1 when unmodelled. */
  iceFactor?: number;
}

export interface PowerOutput {
  /** Propulsive power at the crank (ICE + deploy − super-clip harvest), W. */
  propulsiveW: number;
  /** Power the MGU-K is deploying (+) or harvesting (−), W. */
  mguKW: number;
  /** Harvest power that must slow the car when lifting off (engine-braking feel), W. */
  regenDragW: number;
}

/**
 * Energy store with automatic deployment. A deliberately simple strategy:
 * deploy at full throttle while charge lasts, harvest under braking and when
 * lifting, and "super-clip" (harvest at full throttle) near top speed once the
 * store runs low, which is the characteristic 2026 behaviour on long straights.
 */
export class PowerUnit {
  /** Usable charge, J, in [0, STORE_WINDOW_MJ]. */
  charge = STORE_WINDOW_MJ * MJ;
  /** Energy harvested so far this lap, J (C5.2.10 limit). */
  harvestedThisLap = 0;
  /** Per-lap harvest limit override, MJ (race 8.5 vs qualifying 7.5); undefined uses the param. */
  harvestLimitMJ: number | undefined;
  /**
   * Standing start: the MGU-K may not deploy until the car reaches 50 km/h
   * (C5.2.12). Set by the grid start, cleared at 50 km/h.
   */
  standingStart = false;

  resetLap(): void {
    this.harvestedThisLap = 0;
  }

  get chargeFraction(): number {
    return this.charge / (STORE_WINDOW_MJ * MJ);
  }

  step(req: PowerRequest, dt: number): PowerOutput {
    const speedKmh = req.speed * 3.6;
    const ice = powerParams.icePowerKW.value * KW * req.throttle * (req.iceFactor ?? 1);
    let mguK = 0;

    if (req.brakeForce > 0) {
      mguK = -Math.min(ERS_MAX_KW * KW, req.brakeForce * req.speed);
    } else if (req.throttle < 0.05) {
      mguK = -powerParams.coastHarvestKW.value * KW;
    } else if (this.standingStart && speedKmh < 50) {
      mguK = 0;
    } else {
      this.standingStart = false;
      const cap = req.deployCapKW ?? Infinity;
      const limit = Math.min(ersDeployLimitKW(speedKmh, req.overtake), cap) * KW * req.throttle;
      if (this.chargeFraction < 0.03 && speedKmh > 250) {
        mguK = -powerParams.superClipKW.value * KW;
      } else {
        // Energy is limited per lap, so spend it where it buys the most time:
        // full deployment accelerating out of slow corners, less at high speed.
        // Also taper as the store empties instead of cutting off abruptly.
        mguK = limit * deployWeight(speedKmh) * Math.min(1, this.chargeFraction / 0.1);
      }
    }

    if (mguK < 0) {
      const harvestRoom = Math.max(0, (this.harvestLimitMJ ?? powerParams.harvestPerLapMJ.value) * MJ - this.harvestedThisLap);
      const storeRoom = STORE_WINDOW_MJ * MJ - this.charge;
      const maxHarvest = Math.min(harvestRoom, storeRoom) / dt;
      mguK = -Math.min(-mguK, maxHarvest);
      this.harvestedThisLap += -mguK * dt;
    } else {
      mguK = Math.min(mguK, this.charge / dt);
    }
    this.charge = Math.min(STORE_WINDOW_MJ * MJ, Math.max(0, this.charge - mguK * dt));

    // Under braking, harvest comes out of the braking force, not the drivetrain.
    const braking = req.brakeForce > 0;
    const net = ice + mguK;
    return {
      propulsiveW: braking ? 0 : Math.max(0, net),
      mguKW: mguK,
      regenDragW: braking ? 0 : Math.max(0, -net),
    };
  }
}
